import { useEffect, useState } from "react";
import api, { fmtINR } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, FileDown, Trash2, Printer, MessageSquare, Wand2 } from "lucide-react";
import { exportToExcel } from "@/lib/excel";
import { whatsappSend } from "@/lib/pdf";
import { firmSig } from "@/lib/messages";
import { usePinPrompt } from "@/components/app/PinPrompt";

const emptyForm = {
  party_id: null, party_name: "", amount: 0, payment_mode: "cash",
  payment_ref: "", bank_id: null, notes: "",
};

export default function Payments({ direction /* "in" | "out" */ }) {
  const pinPrompt = usePinPrompt();
  const [rows, setRows] = useState([]);
  const [parties, setParties] = useState([]);
  const [banks, setBanks] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(emptyForm);
  const [unpaid, setUnpaid] = useState([]);          // party's unpaid bills
  const [creditDays, setCreditDays] = useState(0);
  const [alloc, setAlloc] = useState({});             // { [txnId]: amount }
  const [loadingBills, setLoadingBills] = useState(false);

  const load = async () => {
    const type = direction === "in" ? "payment_in" : "payment_out";
    const { data } = await api.get(`/txns?txn_type=${type}`);
    setRows(data);
  };
  useEffect(() => {
    (async () => {
      const [p, b] = await Promise.all([api.get("/parties"), api.get("/banks")]);
      setParties(p.data); setBanks(b.data); load();
    })();
    // eslint-disable-next-line
  }, [direction]);

  const fetchUnpaid = async (partyId) => {
    if (!partyId) { setUnpaid([]); setAlloc({}); setCreditDays(0); return; }
    setLoadingBills(true);
    try {
      const { data } = await api.get(`/parties/${partyId}/unpaid-bills?direction=${direction}`);
      setUnpaid(data.bills || []);
      setCreditDays(data.credit_period_days || 0);
      setAlloc({});
    } catch (e) { setUnpaid([]); }
    finally { setLoadingBills(false); }
  };

  const selectParty = (partyId) => {
    const p = parties.find((x) => x._id_str === partyId);
    setF({ ...f, party_id: p?._id_str || null, party_name: p?.name || "" });
    fetchUnpaid(partyId);
  };

  const setAllocation = (txnId, amount) => {
    const v = Math.max(0, Number(amount) || 0);
    const next = { ...alloc, [txnId]: v };
    if (!v) delete next[txnId];
    setAlloc(next);
  };

  const autoDistribute = () => {
    // FIFO: distribute f.amount across oldest unpaid bills first
    let remaining = Number(f.amount) || 0;
    const next = {};
    for (const b of unpaid) {
      if (remaining <= 0) break;
      const take = Math.min(remaining, b.balance);
      next[b._id_str] = +take.toFixed(2);
      remaining -= take;
    }
    setAlloc(next);
  };

  const allocationsList = Object.entries(alloc)
    .map(([txn_id, amount]) => ({ txn_id, amount: Number(amount) }))
    .filter((a) => a.amount > 0);
  const allocatedTotal = allocationsList.reduce((s, a) => s + a.amount, 0);
  const onAccount = Math.max(0, (Number(f.amount) || 0) - allocatedTotal);
  const overAllocated = allocatedTotal > (Number(f.amount) || 0) + 0.001;

  const save = async () => {
    if (!f.amount || f.amount <= 0) { toast.error("Enter amount"); return; }
    if (overAllocated) { toast.error("Allocated more than amount received"); return; }
    await pinPrompt();
    try {
      await api.post("/txns/payment", {
        ...f,
        direction,
        allocations: allocationsList,
      });
      toast.success(`Payment recorded${allocationsList.length ? ` · ${allocationsList.length} bill(s) settled` : ""}`);
      setOpen(false);
      setF(emptyForm); setUnpaid([]); setAlloc({});
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await pinPrompt();
    try { await api.delete(`/txns/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const openNew = () => { setF(emptyForm); setUnpaid([]); setAlloc({}); setCreditDays(0); setOpen(true); };

  return (
    <div>
      <div className="flex items-end justify-between mb-6 gap-3">
        <div>
          <div className="overline">{direction === "in" ? "Payment In" : "Payment Out"}</div>
          <h1 className="text-3xl font-bold tracking-tight">{direction === "in" ? "Money received from parties" : "Money paid to parties"}</h1>
        </div>
        <div className="flex gap-2 no-print">
          <Button variant="outline" onClick={() => exportToExcel(rows, `payment-${direction}.xlsx`)}><FileDown size={14} /> Excel</Button>
          <Button className="btn-primary" onClick={openNew} data-testid={`pay-${direction}-add`}><Plus size={14} /> Record</Button>
        </div>
      </div>

      <div className="bg-white border border-border rounded-lg overflow-x-auto">
        <table className="acc-table w-full">
          <thead><tr><th>Date</th><th>Number</th><th>Party</th><th>Mode</th><th className="num">Amount</th><th>Ref</th><th>Actions</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">No payments yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r._id_str} data-testid={`pay-row-${r._id_str}`}>
                <td>{new Date(r.date).toLocaleDateString()}</td>
                <td className="font-mono">{r.number}</td>
                <td>
                  {r.party_name || "—"}
                  {r.allocations?.length > 0 && <div className="text-[10px] text-slate-500">{r.allocations.length} bill(s)</div>}
                </td>
                <td className="uppercase text-xs">{r.payment_mode}</td>
                <td className="num">{fmtINR(r.total)}</td>
                <td className="font-mono text-xs">{r.payment_ref || "—"}</td>
                <td>
                  <div className="flex gap-1">
                    <button className="p-1 hover:bg-slate-100 rounded" onClick={() => window.open(`/print/txn/${r._id_str}`, "_blank")} data-testid={`pay-print-${r._id_str}`}><Printer size={14} /></button>
                    <button className="p-1 hover:bg-slate-100 rounded" onClick={async () => {
                      let firm = null; try { firm = (await api.get("/firms/me")).data; } catch {}
                      const amt = Number(r.total || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                      const dt = new Date(r.date).toLocaleDateString();
                      const ref = r.payment_ref ? ` (Ref: ${r.payment_ref})` : "";
                      const draft = direction === "in"
                        ? `Dear ${r.party_name || "Sir/Madam"},\n\nWe have received ₹${amt} from you on ${dt} via ${r.payment_mode?.toUpperCase() || "—"}${ref}. Receipt No: ${r.number}.\n\nThank you for the payment.${firmSig(firm)}`
                        : `Dear ${r.party_name || "Sir/Madam"},\n\nWe have paid ₹${amt} to you on ${dt} via ${r.payment_mode?.toUpperCase() || "—"}${ref}. Voucher No: ${r.number}.${firmSig(firm)}`;
                      const body = window.prompt("WhatsApp text (edit if needed):", draft);
                      if (body) {
                        const phone = parties.find(p => p._id_str === r.party_id)?.phone;
                        whatsappSend(phone, body);
                      }
                    }} data-testid={`pay-msg-${r._id_str}`}><MessageSquare size={14} /></button>
                    <button className="p-1 hover:bg-red-50 text-red-600 rounded" onClick={() => del(r._id_str)}><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{direction === "in" ? "Record Payment In" : "Record Payment Out"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Party</Label>
              <select className="w-full border border-border rounded-md h-9 px-2 bg-white" value={f.party_id || ""} onChange={(e) => selectParty(e.target.value)} data-testid="pay-party">
                <option value="">— select —</option>
                {parties.map((p) => <option key={p._id_str} value={p._id_str}>{p.name}</option>)}
              </select>
              {creditDays > 0 && <div className="text-[10px] text-slate-500 mt-1">Credit period: {creditDays} days</div>}
            </div>
            <div><Label>Amount received</Label><Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: +e.target.value })} data-testid="pay-amount" /></div>
            <div><Label>Mode</Label>
              <select className="w-full border border-border rounded-md h-9 px-2 bg-white" value={f.payment_mode} onChange={(e) => setF({ ...f, payment_mode: e.target.value })}>
                <option value="cash">Cash</option>
                <option value="upi">UPI</option>
                <option value="bank">Bank Transfer</option>
                <option value="cheque">Cheque</option>
              </select>
            </div>
            {(f.payment_mode === "bank" || f.payment_mode === "upi") && (
              <div><Label>Bank</Label>
                <select className="w-full border border-border rounded-md h-9 px-2 bg-white" value={f.bank_id || ""} onChange={(e) => setF({ ...f, bank_id: e.target.value || null })}>
                  <option value="">— select —</option>
                  {banks.map((b) => <option key={b._id_str} value={b._id_str}>{b.name}</option>)}
                </select>
              </div>
            )}
            <div><Label>Reference</Label><Input value={f.payment_ref} onChange={(e) => setF({ ...f, payment_ref: e.target.value })} /></div>
            <div className="col-span-2"><Label>Notes</Label><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
          </div>

          {/* Bill allocation */}
          {f.party_id && (
            <div className="mt-4 border-t border-border pt-4">
              <div className="flex items-center justify-between mb-2">
                <div className="overline">Allocate to Unpaid {direction === "in" ? "Invoices" : "Bills"}</div>
                <Button size="sm" variant="outline" onClick={autoDistribute} disabled={!f.amount || unpaid.length === 0} data-testid="pay-auto-distribute">
                  <Wand2 size={12} className="mr-1" /> Auto (FIFO)
                </Button>
              </div>
              {loadingBills && <div className="text-sm text-muted-foreground">Loading bills…</div>}
              {!loadingBills && unpaid.length === 0 && (
                <div className="text-sm text-muted-foreground bg-slate-50 border border-border rounded p-3">
                  No unpaid {direction === "in" ? "invoices" : "bills"} for this party. Payment will be recorded on-account.
                </div>
              )}
              {!loadingBills && unpaid.length > 0 && (
                <div className="border border-border rounded-md overflow-hidden">
                  <table className="acc-table w-full text-sm">
                    <thead>
                      <tr>
                        <th style={{ width: 32 }}></th>
                        <th>Invoice</th><th>Date</th>
                        <th className="num">Total</th>
                        <th className="num">Paid</th>
                        <th className="num">Balance</th>
                        <th className="num">Overdue</th>
                        <th className="num" style={{ width: 130 }}>Allocate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unpaid.map((b) => (
                        <tr key={b._id_str} data-testid={`pay-bill-${b._id_str}`}>
                          <td>
                            <input
                              type="checkbox"
                              checked={!!alloc[b._id_str]}
                              onChange={(e) => setAllocation(b._id_str, e.target.checked ? b.balance : 0)}
                              data-testid={`pay-bill-check-${b._id_str}`}
                            />
                          </td>
                          <td className="font-mono">{b.number}</td>
                          <td>{new Date(b.date).toLocaleDateString()}</td>
                          <td className="num">{fmtINR(b.total)}</td>
                          <td className="num">{fmtINR(b.paid)}</td>
                          <td className="num font-semibold">{fmtINR(b.balance)}</td>
                          <td className="num">
                            {b.days_overdue > 0 ? (
                              <span className="chip chip-red">{b.days_overdue}d</span>
                            ) : (
                              <span className="text-slate-400 text-xs">—</span>
                            )}
                          </td>
                          <td>
                            <Input
                              type="number"
                              className="h-7 text-right font-mono"
                              value={alloc[b._id_str] || ""}
                              placeholder="0"
                              max={b.balance}
                              onChange={(e) => setAllocation(b._id_str, Math.min(+e.target.value, b.balance))}
                              data-testid={`pay-bill-amt-${b._id_str}`}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="mt-3 grid grid-cols-3 gap-3 text-sm font-mono">
                <div className="border border-border rounded-md p-2">
                  <div className="overline text-[10px]">Amount</div>
                  <div className="font-bold">{fmtINR(f.amount)}</div>
                </div>
                <div className="border border-border rounded-md p-2">
                  <div className="overline text-[10px]">Allocated</div>
                  <div className={`font-bold ${overAllocated ? "text-[#991B1B]" : "text-[#064E3B]"}`} data-testid="pay-allocated">{fmtINR(allocatedTotal)}</div>
                </div>
                <div className="border border-border rounded-md p-2">
                  <div className="overline text-[10px]">On Account</div>
                  <div className="font-bold text-slate-700" data-testid="pay-on-account">{fmtINR(onAccount)}</div>
                </div>
              </div>
              {overAllocated && (
                <div className="mt-2 text-xs text-[#991B1B]">
                  ⚠ Allocated amount exceeds received amount. Reduce allocations or increase received amount.
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="btn-primary" onClick={save} disabled={overAllocated} data-testid="pay-save">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
